import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";
import { useScrollReveal } from "../components/useScrollReveal";
import { useSEO } from "../components/useSEO";
import { fetchPostList, blogConfigured, formatDate } from "../blogData";
import "./Resources.css";

/**
 * Real blog index — fetches real posts from a connected GitHub repo,
 * same logic as the original blog.js. Until a real repo is connected
 * (blogConfigured() is false), this shows the one real fallback demo
 * post plus an honest "Preview mode" notice — never a fake "it's live"
 * state. Post links go to /blog/:slug (a real route), not the
 * original's ?post= query param — cleaner URLs, same destination.
 */
export default function Resources() {
  useScrollReveal();
  useSEO(
    "Resources | Articles & Insights from Modus",
    "Articles, research, and ideas from Modus on physician quality, provider data management, and measuring patient outcomes instead of billing codes."
  );
  const [posts, setPosts] = useState(null); // null = still loading
  const [configured] = useState(blogConfigured());
  // Some cover URLs still point at the old WordPress media library
  // (modus.org/wp-content/uploads/...), which no longer serves those
  // files now that this site replaced WordPress. Track which covers
  // failed to load so we fall back to the placeholder instead of a
  // broken-image icon — the real fix is re-pointing each post's
  // post-cover meta tag in the modus-blog content repo to a live URL.
  const [failedCovers, setFailedCovers] = useState(() => new Set());

  useEffect(() => {
    let cancelled = false;
    fetchPostList().then((list) => {
      if (!cancelled) setPosts(list);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <SiteHeader active="resources" />

      <section className="res-hero reveal">
        <div className="wrap">
          <p className="kicker">Resources</p>
          <h1>Articles, news and ideas from Modus.</h1>
          <p>
            Notes on value-based care, direct contracting, and what the data actually says about
            physician quality.
          </p>
          {!configured && (
            <span className="preview-notice" style={{ display: "inline-flex" }}>
              ● Preview mode: connect a GitHub repo to go live
            </span>
          )}
        </div>
      </section>

      <section>
        <div className="wrap">
          {posts === null ? (
            <div className="blog-empty" style={{ display: "block" }}>
              Loading posts…
            </div>
          ) : posts.length === 0 ? (
            <div className="blog-empty" style={{ display: "block" }}>
              No posts yet. Add one to the connected repo's <code>/blog</code> folder to get
              started.
            </div>
          ) : (
            <div className="blog-grid">
              {posts.map((post, i) => (
                <Link
                  key={post.slug}
                  className="post-card post-card-in"
                  style={{ animationDelay: `${Math.min(i, 12) * 0.06}s` }}
                  to={`/blog/${encodeURIComponent(post.slug)}`}
                >
                  {post.cover && !failedCovers.has(post.slug) ? (
                    <img
                      src={post.cover}
                      alt=""
                      className="post-card-img"
                      onError={() =>
                        setFailedCovers((prev) => new Set(prev).add(post.slug))
                      }
                    />
                  ) : (
                    <div className="post-card-img post-card-img-placeholder">
                      <span>Modus</span>
                    </div>
                  )}
                  <div className="post-card-body">
                    <div className="post-card-date">{formatDate(post.date)}</div>
                    <h2 className="post-card-title">{post.title}</h2>
                    <p className="post-card-excerpt">{post.excerpt}</p>
                    <span className="post-card-more">Read more →</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <SiteFooter />
    </>
  );
}